import { Box, Card, CardContent, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import Hls from 'hls.js';
import { useLocations } from './LocationContext';
import { ILocationCameraProps } from '../../../services/Location/types';
import { getMoovsecVehicleRealtimeCamerasAsync } from '../../../services/Location';

interface VehicleCamerasLivePanelProps {
    selectedId?: string | null;
    embedded?: boolean;
}

const MAX_CAMERA_SLOTS = 4;
const CHANNEL_SORT_FALLBACK = 999;
const REALTIME_REFRESH_MS = 20000;
const REALTIME_REFRESH_HIDDEN_MS = 45000;
const REALTIME_REFRESH_MAX_BACKOFF_MS = 90000;
const HLS_RESTART_MAX_ATTEMPTS = 3;

const sortByChannel = (items: ILocationCameraProps[]) =>
    items.slice().sort((a, b) => (a.channel ?? CHANNEL_SORT_FALLBACK) - (b.channel ?? CHANNEL_SORT_FALLBACK));

const toFixedSlots = (cameras: ILocationCameraProps[]) =>
    Array.from({ length: MAX_CAMERA_SLOTS }, (_, index) => cameras[index] || null);

const isImageUrl = (url?: string) => {
    if (!url) return false;

    return /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(url);
};

const AutoPlayVideo = ({ src, isHls }: { src: string; isHls: boolean }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const hlsRef = useRef<InstanceType<typeof Hls> | null>(null);
    const hlsRecoveryRef = useRef({ networkErrors: 0, mediaErrors: 0 });
    const hlsRestartRef = useRef(0);
    const hlsRestartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) {
            return;
        }

        video.muted = true;

        const playVideo = () => {
            const promise = video.play();
            if (promise && typeof promise.catch === 'function') {
                promise.catch(() => {
                });
            }
        };

        const retryNativeStream = () => {
            if (isHls) {
                return;
            }

            video.load();
            playVideo();
        };

        const destroyHls = () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };

        const clearHlsRestartTimer = () => {
            if (hlsRestartTimerRef.current) {
                clearTimeout(hlsRestartTimerRef.current);
                hlsRestartTimerRef.current = null;
            }
        };

        const attachHlsIfNeeded = () => {
            destroyHls();

            if (!isHls) {
                video.src = src;
                return;
            }

            if (video.canPlayType('application/vnd.apple.mpegurl')) {
                video.src = src;
                return;
            }

            if (!Hls.isSupported()) {
                video.src = src;
                return;
            }

            const hls = new Hls({
                manifestLoadingTimeOut: 10000,
                levelLoadingTimeOut: 10000,
                fragLoadingTimeOut: 10000,
                maxBufferLength: 20,
            });

            hlsRef.current = hls;
            hls.attachMedia(video);
            hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                hls.loadSource(src);
            });

            hls.on(Hls.Events.LEVEL_LOADED, () => {
                hlsRecoveryRef.current.networkErrors = 0;
                hlsRecoveryRef.current.mediaErrors = 0;
                hlsRestartRef.current = 0;
            });

            hls.on(Hls.Events.ERROR, (_event: string, data: { fatal?: boolean; type?: string }) => {
                if (!data?.fatal) {
                    return;
                }

                if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
                    if (hlsRecoveryRef.current.networkErrors < 3) {
                        hlsRecoveryRef.current.networkErrors += 1;
                        hls.startLoad();
                        return;
                    }
                }

                if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                    if (hlsRecoveryRef.current.mediaErrors < 2) {
                        hlsRecoveryRef.current.mediaErrors += 1;
                        hls.recoverMediaError();
                        return;
                    }
                }

                if (hlsRestartRef.current < HLS_RESTART_MAX_ATTEMPTS) {
                    hlsRestartRef.current += 1;
                    clearHlsRestartTimer();
                    destroyHls();

                    hlsRestartTimerRef.current = setTimeout(() => {
                        attachHlsIfNeeded();
                        playVideo();
                    }, 600 * hlsRestartRef.current);

                    return;
                }

                destroyHls();
            });
        };

        hlsRecoveryRef.current.networkErrors = 0;
        hlsRecoveryRef.current.mediaErrors = 0;
        hlsRestartRef.current = 0;
        clearHlsRestartTimer();
        attachHlsIfNeeded();
        playVideo();
        video.addEventListener('loadedmetadata', playVideo);
        video.addEventListener('canplay', playVideo);
        video.addEventListener('waiting', retryNativeStream);
        video.addEventListener('stalled', retryNativeStream);
        video.addEventListener('error', retryNativeStream);

        return () => {
            destroyHls();
            clearHlsRestartTimer();

            video.removeEventListener('loadedmetadata', playVideo);
            video.removeEventListener('canplay', playVideo);
            video.removeEventListener('waiting', retryNativeStream);
            video.removeEventListener('stalled', retryNativeStream);
            video.removeEventListener('error', retryNativeStream);
        };
    }, [isHls, src]);

    return (
        <video
            ref={videoRef}
            muted
            autoPlay
            controls
            playsInline
            preload="auto"
            style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#060b14' }}
        />
    );
};

const CameraCard = ({ camera, compact = false }: { camera: ILocationCameraProps; compact?: boolean }) => {
    const normalizedStatus = (camera.status || '').toLowerCase();
    const isThumbnail = normalizedStatus === 'thumbnail';
    const hlsCandidate = camera.hlsUrl || (camera.streamUrl?.toLowerCase().includes('.m3u8') ? camera.streamUrl : undefined);
    const videoCandidate = hlsCandidate || camera.streamUrl;
    const mediaUrl = isThumbnail ? camera.snapshotUrl : (videoCandidate || camera.snapshotUrl);
    const imageOnly = isThumbnail || isImageUrl(mediaUrl);
    const isRtspOnly = !videoCandidate && !!camera.rtspUrl && !camera.snapshotUrl;
    const isHls = !!hlsCandidate;

    return (
        <Card
            variant="outlined"
            sx={{
                width: '100%',
                minWidth: 0,
                backgroundColor: '#101521',
                borderColor: '#1e2a3d',
            }}
        >
            <CardContent sx={{ p: compact ? 1 : 1.5 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1,
                        gap: 1,
                    }}
                >
                    <Typography
                        sx={{
                            color: '#e8edf6',
                            fontSize: compact ? 12 : 13,
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {camera.name || camera.cameraId || 'Camera'}
                    </Typography>

                    <Chip
                        size="small"
                        label={camera.isOnline === false ? 'offline' : 'online'}
                        sx={{
                            fontSize: 11,
                            color: '#f5f7fb',
                            backgroundColor: camera.isOnline === false ? '#a01f2d' : '#156f46',
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        width: '100%',
                        aspectRatio: '16 / 9',
                        minHeight: compact ? 145 : 170,
                        borderRadius: 1,
                        background: '#060b14',
                        border: '1px solid #1d2635',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {!mediaUrl && !isRtspOnly && (
                        <Typography sx={{ color: '#8491a8', fontSize: 12 }}>
                            Sem URL de stream para esta camera.
                        </Typography>
                    )}

                    {isRtspOnly && (
                        <Typography sx={{ color: '#8491a8', fontSize: 12, textAlign: 'center', px: 1 }}>
                            Stream RTSP nao suportado no navegador.
                        </Typography>
                    )}

                    {mediaUrl && imageOnly && (
                        <img
                            src={mediaUrl}
                            alt={camera.name || camera.cameraId || 'camera'}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#060b14', display: 'block' }}
                        />
                    )}

                    {mediaUrl && !imageOnly && (
                        <AutoPlayVideo src={mediaUrl} isHls={isHls} />
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

const CameraPlaceholderCard = ({ index, compact = false }: { index: number; compact?: boolean }) => (
    <Card
        variant="outlined"
        sx={{
            width: '100%',
            minWidth: 0,
            backgroundColor: '#0d1320',
            borderColor: '#1e2a3d',
            opacity: 0.7,
        }}
    >
        <CardContent sx={{ p: compact ? 1 : 1.5 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                    gap: 1,
                }}
            >
                <Typography sx={{ color: '#8a97ad', fontSize: 13, fontWeight: 600 }}>
                    Canal {index + 1}
                </Typography>
                <Chip
                    size="small"
                    label="indisponivel"
                    sx={{ fontSize: 11, color: '#d6dde8', backgroundColor: '#3a4558' }}
                />
            </Box>

            <Box
                sx={{
                    width: '100%',
                    aspectRatio: '16 / 9',
                    minHeight: compact ? 145 : 170,
                    borderRadius: 1,
                    background: '#060b14',
                    border: '1px dashed #2a364b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography sx={{ color: '#6f7f9a', fontSize: 12 }}>
                    Sem sinal
                </Typography>
            </Box>
        </CardContent>
    </Card>
);

const VehicleCamerasLivePanel = ({ selectedId, embedded = false }: VehicleCamerasLivePanelProps) => {
    const locations = useLocations();
    const [liveCameras, setLiveCameras] = useState<ILocationCameraProps[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isExpanded, setIsExpanded] = useState(true);
    const [refreshCounter, setRefreshCounter] = useState(0);
    const requestIdRef = useRef(0);
    const compactLayout = embedded;

    const selectedLocation = useMemo(() => {
        if (!selectedId) return undefined;

        return locations.find((location) => location.entityId === selectedId);
    }, [locations, selectedId]);

    useEffect(() => {
        if (selectedId) {
            setIsExpanded(true);
        }
    }, [selectedId]);

    useEffect(() => {
        if (!selectedId) {
            setLiveCameras([]);
            return;
        }

        let isCancelled = false;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let isFetching = false;
        let failureCount = 0;
        let lastFetchAt = 0;

        const getNextDelay = () => {
            const baseDelay = document.visibilityState === 'hidden'
                ? REALTIME_REFRESH_HIDDEN_MS
                : REALTIME_REFRESH_MS;

            if (failureCount <= 0) {
                return baseDelay;
            }

            return Math.min(
                REALTIME_REFRESH_MAX_BACKOFF_MS,
                baseDelay * Math.pow(2, Math.min(failureCount, 3)),
            );
        };

        const scheduleNext = () => {
            if (isCancelled) {
                return;
            }

            timeoutId = setTimeout(() => {
                void fetchRealtimeCameras(false);
            }, getNextDelay());
        };

        const fetchRealtimeCameras = async (showLoading: boolean) => {
            if (isFetching) {
                scheduleNext();
                return;
            }

            isFetching = true;
            const requestId = requestIdRef.current + 1;
            requestIdRef.current = requestId;
            lastFetchAt = Date.now();

            if (showLoading) {
                setIsLoading(true);
            }

            try {
                const response = await getMoovsecVehicleRealtimeCamerasAsync(selectedId, MAX_CAMERA_SLOTS);
                if (isCancelled || requestId !== requestIdRef.current) {
                    return;
                }

                const next = sortByChannel(response || []);
                setLiveCameras(next);
                failureCount = 0;
            } catch {
                if (isCancelled || requestId !== requestIdRef.current) {
                    return;
                }

                failureCount += 1;
                setLiveCameras([]);
            } finally {
                isFetching = false;
                if (!isCancelled && showLoading) {
                    setIsLoading(false);
                }

                scheduleNext();
            }
        };

        const handleVisibilityChange = () => {
            if (isCancelled || document.visibilityState !== 'visible') {
                return;
            }

            if (Date.now() - lastFetchAt < 4000) {
                return;
            }

            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }

            void fetchRealtimeCameras(false);
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        void fetchRealtimeCameras(true);

        return () => {
            isCancelled = true;

            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [selectedId, refreshCounter]);

    const cameras = liveCameras.length > 0 ? liveCameras : (selectedLocation?.cameras || []);
    const fixedCameraSlots = useMemo<(ILocationCameraProps | null)[]>(() => {
        return toFixedSlots(cameras);
    }, [cameras]);

    const panelStatusMessage = useMemo(() => {
        if (!isExpanded) {
            return 'Exibicao de cameras recolhida.';
        }
        if (cameras.length === 0) {
            return 'Nenhuma camera disponivel para este veiculo.';
        }
        return null;
    }, [cameras.length, isExpanded]);

    if (!selectedId) {
        return null;
    }

    const handleRefresh = () => {
        if (isLoading) {
            return;
        }

        setRefreshCounter((prev) => prev + 1);
    };

    return (
        <Box
            sx={{
                borderRadius: compactLayout ? 1.5 : 2,
                border: '1px solid #1f2b40',
                background:
                    'linear-gradient(180deg, rgba(9,16,28,0.95) 0%, rgba(7,12,22,0.95) 100%)',
                backdropFilter: embedded ? 'blur(3px)' : 'none',
                p: compactLayout ? 1 : 1.5,
                mt: embedded ? 0 : 1,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ color: '#f4f7ff', fontWeight: 700, fontSize: compactLayout ? 13 : 14 }}>
                    Cameras Ao Vivo - {selectedLocation?.vehiclePlate || selectedId}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Atualizar transmissões">
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleRefresh}
                                disabled={isLoading}
                                sx={{ color: '#d7deeb' }}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title={isExpanded ? 'Fechar cameras' : 'Abrir cameras'}>
                        <IconButton
                            size="small"
                            onClick={() => setIsExpanded((prev) => !prev)}
                            sx={{ color: '#d7deeb' }}
                        >
                            {isExpanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {panelStatusMessage && (
                <Typography sx={{ color: '#95a2ba', fontSize: 12 }}>
                    {panelStatusMessage}
                </Typography>
            )}

            {isExpanded && cameras.length > 0 && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                        gap: compactLayout ? 1 : 1.5,
                        pt: 0.5,
                        overflowX: 'auto',
                        minWidth: 0,
                        '& > *': {
                            minWidth: compactLayout ? 205 : 230,
                        },
                    }}
                >
                    {fixedCameraSlots.map((camera, index) => (
                        camera ? (
                            <CameraCard
                                key={`${camera.cameraId || camera.name || 'camera'}-${index}-${refreshCounter}`}
                                camera={camera}
                                compact={compactLayout}
                            />
                        ) : (
                            <CameraPlaceholderCard key={`camera-placeholder-${index}`} index={index} compact={compactLayout} />
                        )
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default VehicleCamerasLivePanel;
